<!--
  OrderDailyView.vue — 全收款统计页面
  展示每日全平台汇总的收款、退款、首购、续订数据。
  功能：日期范围筛选、分页查询、CSV 下载。
  接口：GET /v1/analytics/order/stats/daily/total/payment
-->
<template>
  <div>
    <!-- ===== 搜索栏：日期范围筛选 + 查询/重置/下载 ===== -->
    <a-card
      :bordered="false"
      style="margin-bottom: 16px"
    >
      <a-form
        layout="inline"
        :model="searchForm"
      >
        <a-form-item label="日期范围">
          <a-range-picker
            v-model:value="dateRange"
            value-format="YYYY-MM-DD"
            :allow-clear="true"
          />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button
              type="primary"
              :loading="loading"
              @click="handleSearch"
            >查询</a-button>
            <a-button @click="handleReset">重置</a-button>
            <a-button
              :loading="downloading"
              @click="handleDownload"
            >下载</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- ===== 数据表格 ===== -->
    <a-card
      :bordered="false"
      title="全收款统计"
    >
      <a-table
        :columns="columns"
        :data-source="tableData"
        :loading="loading"
        :pagination="pagination"
        row-key="statDate"
        @change="handleTableChange"
        :scroll="{ x: 'max-content' }"
      />
    </a-card>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { message } from "ant-design-vue";
import { analyticsUrl } from "@/utils/api";
import { get } from "@/utils/request";

/** 表格加载状态 */
const loading = ref(false);
/** CSV 下载加载状态 */
const downloading = ref(false);
/** 日期范围选择器绑定值 */
const dateRange = ref([]);
/** 表格数据 */
const tableData = ref([]);

/** Ant Design 表格分页配置（双向同步） */
const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total) => `共 ${total} 条`,
});

/** 搜索表单数据模型 */
const searchForm = reactive({
  startDate: "",
  endDate: "",
});

/**
 * 金额格式化：分 → 元
 * @param {number|null} fen - 金额（单位：分）
 * @returns {string} 格式化后的金额字符串，如 "¥1,234.56"
 */
const formatAmount = (fen) => {
  if (fen == null) return "-";
  return (
    "¥" +
    (fen / 100).toLocaleString("zh-CN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

/** 单元格不换行样式，应用于表头和单元格 */
const nowrap = { style: { whiteSpace: "nowrap" } };

/** 表格列定义 */
const columns = [
  {
    title: "数据日期",
    dataIndex: "statDate",
    key: "statDate",
    width: 110,
    fixed: "left",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
  {
    title: "收款单数",
    dataIndex: "payOrderCount",
    key: "payOrderCount",
    width: 100,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => text?.toLocaleString() ?? "-",
  },
  {
    title: "收款金额",
    dataIndex: "payAmount",
    key: "payAmount",
    width: 130,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatAmount(text),
  },
  {
    title: "退款单数",
    dataIndex: "refundOrderCount",
    key: "refundOrderCount",
    width: 100,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => text?.toLocaleString() ?? "-",
  },
  {
    title: "退款金额",
    dataIndex: "refundAmount",
    key: "refundAmount",
    width: 130,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatAmount(text),
  },
  {
    title: "初次购买单数",
    dataIndex: "firstPayOrderCount",
    key: "firstPayOrderCount",
    width: 120,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => text?.toLocaleString() ?? "-",
  },
  {
    title: "初次购买金额",
    dataIndex: "firstPayAmount",
    key: "firstPayAmount",
    width: 130,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatAmount(text),
  },
  {
    title: "续订单数",
    dataIndex: "renewOrderCount",
    key: "renewOrderCount",
    width: 100,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => text?.toLocaleString() ?? "-",
  },
  {
    title: "续订金额",
    dataIndex: "renewAmount",
    key: "renewAmount",
    width: 130,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatAmount(text),
  },
  {
    title: "更新时间",
    dataIndex: "updatedAt",
    key: "updatedAt",
    width: 165,
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
];

/**
 * 获取全收款统计数据
 * @param {number} pageIdx - 页码
 * @param {number} pageSize - 每页条数
 */
const fetchData = async (pageIdx = 1, pageSize = 20) => {
  loading.value = true;
  try {
    const params = {
      pageIdx,
      pageSize,
      startDate: searchForm.startDate || "",
      endDate: searchForm.endDate || "",
    };
    const json = await get(
      analyticsUrl("/order/stats/daily/total/payment"),
      params
    );
    if (json.code === 200 && json.data) {
      const d = json.data;
      tableData.value = d.data;
      pagination.total = d.dataSum;
      pagination.current = d.pageIdx;
      pagination.pageSize = d.pageSize;
    } else {
      message.warning("查询失败：" + (json.content || "未知错误"));
    }
  } catch (e) {
    message.error("接口请求失败");
  } finally {
    loading.value = false;
  }
};

/** 查询按钮：同步日期范围到 searchForm 后重新请求第1页 */
const handleSearch = () => {
  if (dateRange.value && dateRange.value.length === 2) {
    searchForm.startDate = dateRange.value[0];
    searchForm.endDate = dateRange.value[1];
  } else {
    searchForm.startDate = "";
    searchForm.endDate = "";
  }
  fetchData(1, pagination.pageSize);
};

/** 重置按钮：清空筛选条件并重新加载 */
const handleReset = () => {
  dateRange.value = [];
  searchForm.startDate = "";
  searchForm.endDate = "";
  fetchData(1, pagination.pageSize);
};

/** 表格分页变化回调 */
const handleTableChange = (pag) => {
  fetchData(pag.current, pag.pageSize);
};

/**
 * CSV 下载
 * 遍历所有分页数据，拼接为 CSV 文件（BOM + UTF-8）并触发浏览器下载。
 * 金额字段会从分转为元。
 */
const handleDownload = async () => {
  downloading.value = true;
  try {
    const allData = [];
    let pageIdx = 1;
    let pageCount = 1;
    while (pageIdx <= pageCount) {
      const params = {
        pageIdx,
        pageSize: 100,
        startDate: searchForm.startDate || "",
        endDate: searchForm.endDate || "",
      };
      const json = await get(
        analyticsUrl("/order/stats/daily/total/payment"),
        params
      );
      if (json.code !== 200 || !json.data) break;
      allData.push(...json.data.data);
      pageCount = json.data.pageCount;
      pageIdx++;
    }
    if (!allData.length) {
      message.warning("没有数据可下载");
      return;
    }

    const amountKeys = [
      "payAmount",
      "refundAmount",
      "firstPayAmount",
      "renewAmount",
    ];
    const header = columns.map((c) => c.title).join(",");
    const rows = allData.map((r) =>
      columns
        .map((c) => {
          const v = r[c.dataIndex];
          if (v == null) return "";
          if (amountKeys.includes(c.dataIndex)) return (v / 100).toFixed(2);
          return String(v).includes(",") ? `"${v}"` : v;
        })
        .join(",")
    );
    const csv = "\uFEFF" + [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `全收款统计.csv`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    message.error("下载失败");
  } finally {
    downloading.value = false;
  }
};

/** 组件挂载时加载第一页数据 */
onMounted(() => {
  fetchData();
});
</script>
